/**
 * ATL-1445 stage 3, P9 — the seam's waiters: a live master write's promise settles on the SERVER's
 * answer (`docs/technical/write-cutover-stage3-app-seam.md` §1 step 4: "resolve only on the server's
 * acknowledgement"), and rejects on a deterministic refusal while the entry stays held (step 5).
 *
 * A write's promise is held by the screen that made it (New Sale awaits `saveNewSale`); the entry is
 * delivered by the session's drainer — this tab's, or, on web, another tab's (one tab holds the drain
 * lock). So a waiter is settled by whichever of these sees the outcome first:
 *
 *  - this tab's drainer delivered it (`OutboxDrainerOptions.onDelivered`, with the response) →
 *    `delivered`;
 *  - this tab's drainer held it (`onHeld`) → `held`;
 *  - another tab announced the delivery on the local view's BroadcastChannel → `delivered` with no
 *    response (`writeWAckFromChannel`);
 *  - a listing of the queue, begun AFTER the entry was durably queued, no longer holds it → `delivered`
 *    with no response (another tab delivered it and its broadcast was lost — P6 decision 3 applies the
 *    same inference); a listing that shows it `held` → `held` (another tab's drainer held it).
 *
 * What is NOT settled, by design: an entry still pending (offline: the promise waits, as a native
 * Firestore commit does today — no toast, no receipt until reconnect, stage-0 §4.1), and an entry
 * whose queue cannot be read. A waiter never rejects because of time; the cart was cleared before the
 * write and the outbox keeps the write either way.
 *
 * An entry removed WITHOUT delivery — purged by a later queued delete of its document — settles
 * `discarded` (`writeWAckDiscarded`: from the tab that appended the delete, and from the others through
 * the local view's channel), so its writer does not report a success it never had.
 *
 * Residual (stated): a listing that misses an entry whose removal was neither seen nor broadcast reads it
 * as `delivered` (P6 decision 3 makes the same inference for the local view). An operator discard is the
 * one other removal; the operator surface acts only on held and foreign-install entries, and a held
 * entry's waiter was already rejected when it was held.
 *
 * In memory, per JS context. A restart forgets every waiter, and that is correct: the screen that
 * awaited the write is gone too, and the entry is still delivered by the next session's drainer.
 */
import type { OutboxLastError } from './outboxEntry'
import type { OutboxSlot } from './outboxStore'

export type WriteWAckOutcome =
  | { kind: 'delivered'; response: unknown }
  | { kind: 'held'; lastError: OutboxLastError | null }
  /** Removed without delivery: purged by a later queued delete of the same document, or discarded by an operator. */
  | { kind: 'discarded' }

interface Waiter {
  orgFirestoreId: string
  entryId: string
  /**
   * Set once the entry is durably queued: only then may a listing's absence mean "delivered" — and
   * only a listing that BEGAN after it (`writeWAckListingObserver` picks its waiters when it begins).
   */
  queued: boolean
  settle: (outcome: WriteWAckOutcome) => void
}

const waiters = new Map<string, Waiter>()

const keyOf = (orgFirestoreId: string, entryId: string): string => `${orgFirestoreId}\u0000${entryId}`

export interface WriteWAckHandle {
  promise: Promise<WriteWAckOutcome>
  /** The entry is now durably queued (`enqueuePrepared` resolved). */
  markQueued(): void
  /** The write was never queued (the enqueue failed): forget the waiter; its promise never settles. */
  cancel(): void
}

/**
 * Register the waiter for an entry BEFORE it is appended, so a delivery that lands before the append's
 * own promise resolves (a fast drainer) is not missed.
 */
export function awaitWriteWAck(orgFirestoreId: string, entryId: string): WriteWAckHandle {
  const key = keyOf(orgFirestoreId, entryId)
  let settle: (outcome: WriteWAckOutcome) => void = () => undefined
  const promise = new Promise<WriteWAckOutcome>((resolve) => {
    settle = resolve
  })
  const waiter: Waiter = {
    orgFirestoreId,
    entryId,
    queued: false,
    settle: (outcome) => {
      if (waiters.get(key) !== waiter) return
      waiters.delete(key)
      settle(outcome)
    },
  }
  waiters.set(key, waiter)
  return {
    promise,
    markQueued() {
      waiter.queued = true
    },
    cancel() {
      if (waiters.get(key) === waiter) waiters.delete(key)
    },
  }
}

/** This tab's drainer delivered the entry (`onDelivered`). */
export function writeWAckDelivered(orgFirestoreId: string, entryId: string, response: unknown): void {
  waiters.get(keyOf(orgFirestoreId, entryId))?.settle({ kind: 'delivered', response })
}

/** This tab's drainer held the entry (`onHeld`). */
export function writeWAckHeld(orgFirestoreId: string, entryId: string, lastError: OutboxLastError): void {
  waiters.get(keyOf(orgFirestoreId, entryId))?.settle({ kind: 'held', lastError })
}

/** Another tab announced the delivery (the local view's BroadcastChannel). */
export function writeWAckFromChannel(orgFirestoreId: string, entryId: string): void {
  waiters.get(keyOf(orgFirestoreId, entryId))?.settle({ kind: 'delivered', response: undefined })
}

/**
 * Entries removed WITHOUT delivery (a queued delete purged them; another tab says so on the channel):
 * their writers must not report success — a sale voided before it was ever sent must not print its
 * receipt. Called before the listing that would otherwise read the absence as a delivery.
 */
export function writeWAckDiscarded(orgFirestoreId: string, entryIds: readonly string[]): void {
  for (const entryId of entryIds) waiters.get(keyOf(orgFirestoreId, entryId))?.settle({ kind: 'discarded' })
}

/**
 * Called right BEFORE a listing of the organization's queue begins (the drainer's `observeListing`);
 * returns what to call with the listing. Only waiters whose entry was queued before this point are
 * judged by it.
 */
export function writeWAckListingObserver(orgFirestoreId: string): ((slots: readonly OutboxSlot[]) => void) | null {
  const judged: Waiter[] = []
  for (const waiter of waiters.values()) {
    if (waiter.orgFirestoreId === orgFirestoreId && waiter.queued) judged.push(waiter)
  }
  if (judged.length === 0) return null
  return (slots) => {
    const present = new Map<string, OutboxSlot>()
    for (const slot of slots) if (slot.status === 'ok') present.set(slot.entry.entryId, slot)
    const unreadable = slots.some((slot) => slot.status !== 'ok')
    for (const waiter of judged) {
      const slot = present.get(waiter.entryId)
      if (slot && slot.status === 'ok') {
        if (slot.entry.state === 'held') waiter.settle({ kind: 'held', lastError: slot.entry.lastError })
        continue
      }
      // Absent. With an unreadable record in the queue, "absent" cannot be told from "unreadable".
      if (!unreadable) waiter.settle({ kind: 'delivered', response: undefined })
    }
  }
}

/** Diagnostics and tests: how many writes are awaiting the server. */
export function pendingWriteWAcks(): number {
  return waiters.size
}

/** Test seam: a new JS context. */
export function resetWriteWAcksForTests(): void {
  waiters.clear()
}
